import { useFeatureIsOn } from '@growthbook/growthbook-react';
import React, { useEffect, useState } from 'react';
import { useAccount } from '@/account/index';
import { PremiumDataModal, usePremiumDataModal } from '@/components/index';
import { useAppConfigContext } from '@/context/AppConfig';
import { useAppConstantsContext } from '@/context/AppConstants';
import {
  DataCard,
  PremiumPaymentSuccessModal,
  useReportRedirect,
} from '@/reports/index';
import { propertiesTypes } from '@/reports/types';
import { DataList } from '@/theme/index';
import { checkSingleReportVariations } from '@/utils/checkSingleReportVariations';
import { personSearchByBvId } from '@/utils/index';
import { StyledBadge } from '../DeedsSection/DeedsSection';

export interface ResidentCardProps {
  thumbRating?: () => JSX.Element;
  resident: propertiesTypes.ResidentInfo;
}

export function ResidentCard({ thumbRating, resident }: ResidentCardProps) {
  const [isInSingleReportVariation, setIsInSingleReportVariation] =
    useState(false);
  const [paymentShowModalNumber, setPaymentShowModalNumber] =
    useState<number>(0);
  const [isSuccessModalVisible, setIsSuccessModalVisible] =
    useState<boolean>(false);

  const {
    links: { baseUrl },
    featureFlags: { shouldOpenReportInCurrentTab },
    variationPlanNames,
  } = useAppConstantsContext();
  const { trackEvent } = useAppConfigContext();
  const { navigate } = useAppConfigContext().routingUtils;
  const { redirectToSearchPersonUrl } = useReportRedirect();
  const { data: account } = useAccount(true);
  const {
    isModalOpen,
    closeModal,
    triggerModal: triggerPremiumDataModal,
  } = usePremiumDataModal();
  const isInPartialAddressesVariation = useFeatureIsOn('oar-1075');

  const reportVariations = account
    ? checkSingleReportVariations(account, variationPlanNames, {
        vinPlan: '',
        languagePlan: '',
      })
    : null;

  const isInVariation = reportVariations?.isInSingleReportVariation ?? false;

  useEffect(() => {
    if (!account) return;
    setIsInSingleReportVariation(isInVariation);
  }, []);

  if (isInPartialAddressesVariation) {
    resident.personSearch = {
      ...resident.personSearch,
      showPartialAddresses: true,
    };
  }

  const handleClick = () => {
    if (isInSingleReportVariation) {
      triggerPremiumDataModal(true, isInSingleReportVariation);
      return;
    }

    trackEvent(
      'view person report',
      'engagement click',
      'possible residents section'
    );
    personSearchByBvId(
      resident.bvId,
      resident.personSearch,
      redirectToSearchPersonUrl,
      baseUrl,
      shouldOpenReportInCurrentTab
    );
  };

  const clickableStyles = {
    marginBottom: 0,
    wordBreak: 'break-all',
    overflowWrap: 'break-word',
    textDecoration: 'underline',
    '&:hover': {
      cursor: 'pointer',
    },
  };

  const closeModals = () => {
    setIsSuccessModalVisible(false);
    setPaymentShowModalNumber(0);
  };

  const onSuccessPaymentHandler = () => {
    closeModals();
    navigate('/dashboard');
  };

  return (
    <>
      <DataCard
        thumbRating={thumbRating}
        titleProps={{
          title: resident.fullName,
          onClick: handleClick,
          sx: clickableStyles,
        }}
        buttonProps={{
          label: resident.bvId ? 'View Person Report' : 'Search Person',
          onClick: handleClick,
        }}
        titleTag={
          resident.possibleOwner ? (
            <StyledBadge
              label="POSSIBLE OWNER"
              color="secondary"
              className="badge"
            />
          ) : null
        }
      >
        <DataList items={resident.dataListItems} />
      </DataCard>
      <PremiumDataModal
        isOpen={isModalOpen}
        onClose={closeModal}
        paymentShowModalNumber={paymentShowModalNumber}
        setPaymentShowModalNumber={setPaymentShowModalNumber}
        openSuccessModal={() => setIsSuccessModalVisible(true)}
      />
      <PremiumPaymentSuccessModal
        open={isSuccessModalVisible}
        onClose={closeModals}
        onAccept={onSuccessPaymentHandler}
      />
    </>
  );
}
